import { useMsal } from "@azure/msal-react";
import { Button, Menu, MenuItem } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { loginRequest } from "../authConfig";
import { createLogger } from "../helper/logger";

/**
 * Renders a drop down button with child buttons for logging in with a popup or redirect
 * Also supports fake authentication mode for development
 */
export const SignInButton = () => {
	const { instance } = useMsal();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	// Check for fake authentication mode
	const useFakeAuth =
		process.env.REACT_APP_AUTH_FAKE_IT === "TRUE" ||
		process.env.REACT_APP_AUTH_FAKE_IT === "YES";
	// Create logger instance
	const logger = useMemo(() => createLogger("SignInButton"), []);

	// For fake auth, simulate a login on component mount
	useEffect(() => {
		if (useFakeAuth && !instance.getActiveAccount()) {
			// Create a fake account
			const fakeAccount = {
				homeAccountId: "fake-account-id",
				environment: "fake-environment",
				tenantId: "fake-tenant-id",
				username: "FakeUser@example.com",
				localAccountId: "fake-local-id",
				name: "Fake Developer",
			};

			// Set the fake account as active
			try {
				// Some MSAL implementations might allow setting accounts programmatically
				// This is a workaround for development only
				if (typeof instance.setActiveAccount === "function") {
					instance.setActiveAccount(fakeAccount);
				}

				// Store in session for persistence
				sessionStorage.setItem("fakeAuthEnabled", "true");
				sessionStorage.setItem("fakeAuthAccount", JSON.stringify(fakeAccount));

				logger.info("🔑 FAKE AUTHENTICATION ENABLED - Development Mode");

				// Force a reload to ensure the app recognizes the auth state
				if (!sessionStorage.getItem("fakeAuthInitialized")) {
					sessionStorage.setItem("fakeAuthInitialized", "true");
					window.location.reload();
				}
			} catch (error) {
				logger.error("Failed to set fake authentication", error);
			}
		}
	}, [useFakeAuth, instance, logger]);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLogin = async (loginType) => {
		if (instance.getActiveAccount()) {
			return; // Already logged in
		}
		// Handle fake authentication
		if (useFakeAuth) {
			logger.info("Using fake authentication login");
			handleClose();
			window.location.reload(); // Force reload to apply fake auth
			return;
		}

		try {
			// Check if interaction is in progress
			const interactionStatus = instance.inProgress;
			logger.debug("Authentication interaction status", {
				interactionStatus,
				loginRequest,
			});

			handleClose(); // Close the menu before starting login

			if (loginType === "popup") {
				await instance.loginPopup(loginRequest);
			} else if (loginType === "redirect") {
				await instance.loginRedirect(loginRequest);
			}
		} catch (error) {
			if (error.errorCode === "interaction_in_progress") {
				console.log("Another authentication interaction is in progress");
			} else {
				console.error("Login failed:", error);
			}
		}
	};

	// Display different options based on fake auth mode
	return (
		<>
			<Button
				id="basic-button"
				aria-controls={open ? "basic-menu" : undefined}
				aria-haspopup="true"
				aria-expanded={open ? "true" : undefined}
				onClick={useFakeAuth ? () => handleLogin("fake") : handleClick}
				color={useFakeAuth ? "success" : "primary"}
			>
				{useFakeAuth ? "Sign in (Dev Mode)" : "Sign in"}
			</Button>
			{!useFakeAuth && (
				<Menu
					id="basic-menu"
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					MenuListProps={{
						"aria-labelledby": "basic-button",
					}}
				>
					<MenuItem onClick={() => handleLogin("popup")}>Using popup</MenuItem>
					<MenuItem onClick={() => handleLogin("redirect")}>
						Using Redirect
					</MenuItem>
				</Menu>
			)}
		</>
	);
};
